import {Component, Inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {EsignApiService, EsignResponse, EsignStatus, RevocationReason} from '@npck/auth-frontend-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {BusinessErrorCode, CertexCloudEncryptService, downloadBase64, Spinner, toBusinessOrNull} from '@npck/libs/commons';
import {CodeInputComponent} from 'angular-code-input';
import {catchError, map, mergeMap, Observable, tap} from 'rxjs';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {
    ESIGN_ISSUE_ERROR,
    ESIGN_ISSUE_STARTED,
    ESIGN_ISSUE_SUCCEED,
    ESIGN_NO_SIGN,
    ESIGN_PIN_ENTERED,
    ESIGN_PIN_NOT_MATCHED,
    ESIGN_PIN_STARTED,
    ESIGN_PIN_WRONG_PASSWORD,
    ESIGN_SIGN_ERROR,
    ESIGN_SIGN_STARTED,
} from '../../infra/analytics-events';

@Component({
    templateUrl: 'e-sign-dialog.component.html',
    styleUrls: ['e-sign-dialog.component.scss'],
    standalone: false,
})
export class ESignDialogComponent {
    @ViewChild('codeInput') codeInput: CodeInputComponent;
    @ViewChild('codeInputRepeat') codeInputRepeat: CodeInputComponent;
    ESignCodeDialogResultCode = ESignDialogStatus;
    ESignDialogMode = ESignDialogMode;
    codeLength = 4;
    codeError: ESignDialogStatus;
    code: string;
    hasOrgSignScope: boolean;
    codeRepeat: string;
    isAcceptanceApplied: boolean;
    attemptsLeft: number;
    esign: EsignResponse;
    dialogMode: ESignDialogMode;
    esignStatus: EsignStatus;
    private pinEntryStarted: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: ESignDialogData,
        public dialogRef: MatDialogRef<ESignDialogComponent>,
        private esignApiService: EsignApiService,
        private certexCloudEncryptService: CertexCloudEncryptService,
        private analyticsService: AnalyticsService,
        private authHolderService: AuthHolderService,
    ) {
        this.hasOrgSignScope = dialogData.hasOrgSignScope;

        this.esign = dialogData.esign;
        if (!this.esign || this.authHolderService.resetEsign) {
            this.dialogMode = ESignDialogMode.ISSUE_AND_SIGN;
            this.analyticsService.capture(ESIGN_NO_SIGN);
        } else {
            this.esignStatus = this.esign.status;
            if (this.dialogData.target === 'sign') {
                switch (this.esignStatus) {
                    case EsignStatus.ACTIVE:
                        this.dialogMode = ESignDialogMode.SIGN;
                        break;
                    case EsignStatus.BLOCKED:
                        this.dialogMode = ESignDialogMode.REISSUE_INFO;
                        break;
                    case EsignStatus.EXPIRED:
                    case EsignStatus.REVOKED:
                        this.dialogMode = ESignDialogMode.ISSUE_AND_SIGN;
                        break;
                }
            } else {
                switch (this.esignStatus) {
                    case EsignStatus.ACTIVE:
                        this.dialogMode = ESignDialogMode.REVOCATION;
                        break;
                    case EsignStatus.BLOCKED:
                        this.dialogMode = ESignDialogMode.REISSUE;
                        break;
                }
            }
        }
        this.analyticsService.capture(ESIGN_SIGN_STARTED, {esign_status: this.esignStatus});
    }

    isButtonEnabled(): boolean {
        switch (this.dialogMode) {
            case ESignDialogMode.REISSUE_INFO:
                return this.isAcceptanceApplied;
            case ESignDialogMode.SIGN:
                return this.isCodeValid();
            case ESignDialogMode.REVOCATION:
                return this.isCodeValid() && this.isAcceptanceApplied;
            case ESignDialogMode.ISSUE_AND_SIGN:
            case ESignDialogMode.REISSUE_AND_SIGN:
            case ESignDialogMode.REISSUE:
                return this.isCodeValid() && this.isRepeatCodeValid() && this.isAcceptanceApplied;
        }
    }

    isCodeValid(): boolean {
        return this.code?.length === this.codeLength;
    }

    isRepeatCodeValid(): boolean {
        return this.code === this.codeRepeat;
    }

    onCodeComplete($event: string): void {
        this.code = $event;
        if (this.codeInputRepeat) {
            this.codeInputRepeat.focusOnField(0);
        }
    }

    onCodeRepeatComplete($event: string): void {
        this.codeRepeat = $event;
        if (this.code !== this.codeRepeat) {
            this.analyticsService.capture(ESIGN_PIN_NOT_MATCHED, {mode: this.dialogMode});
            this.codeError = ESignDialogStatus.CODES_NOT_MATCHED;
        }
    }

    onCodeChanged(): void {
        if (!this.pinEntryStarted) {
            this.analyticsService.capture(ESIGN_PIN_STARTED, {mode: this.dialogMode});
            this.pinEntryStarted = true;
        }
        this.codeError = null;
    }

    submit(): void {
        if (this.dialogMode === ESignDialogMode.REISSUE_INFO) {
            switch (this.esignStatus) {
                case EsignStatus.ACTIVE:
                case EsignStatus.BLOCKED:
                case EsignStatus.EXPIRED:
                case EsignStatus.REVOKED:
                    this.resetForDialogMode(ESignDialogMode.REISSUE_AND_SIGN);
                    break;
            }
            return;
        }
        this.analyticsService.capture(ESIGN_PIN_ENTERED, {mode: this.dialogMode});
        Spinner.show('eSignInProgress');
        this.certexCloudEncryptService.encryptPassword(this.code)
            .then(keyPass => {
                switch (this.dialogMode) {
                    case ESignDialogMode.ISSUE_AND_SIGN:
                        this.esignApiService.createEsign({keyPass})
                            .subscribe((newEsign) => this.sign(newEsign, keyPass));
                        break;
                    case ESignDialogMode.SIGN:
                        this.sign(this.dialogData.esign, keyPass);
                        break;
                    case ESignDialogMode.REISSUE_AND_SIGN:
                        this.reissue(this.dialogData.esign, keyPass)
                            .subscribe((newEsign) => {
                                if (this.dialogData.target === 'sign') {
                                    this.sign(newEsign, keyPass);
                                } else {
                                    this.revoke(newEsign, newEsign, keyPass);
                                }
                            });
                        break;
                    case ESignDialogMode.REVOCATION:
                        this.revoke(this.dialogData.esign, this.dialogData.esign, keyPass);
                        break;
                    case ESignDialogMode.REISSUE:
                        this.createEsign(keyPass)
                            .subscribe((newEsign) => this.revoke(this.dialogData.esign, newEsign, keyPass));
                        break;
                }
            });
    }

    revoke(currentEsign: EsignResponse, revocationEsign: EsignResponse, keyPass: string): void {
        this.esignApiService.revokeEsign({
            keyName: currentEsign.keyName,
            revocationKeyName: revocationEsign.keyName,
            revocationKeyPass: keyPass,
        }).subscribe({
            next: () => {
                this.dialogRef.close({success: true});
            },
            error: error => {
                Spinner.hide('eSignInProgress');
                this.handleError(error);
            },
        });
    }

    sign(currentEsign: EsignResponse, keyPass: string): void {
        this.esignApiService.sign({
            keyName: currentEsign.keyName,
            keyPass: keyPass,
        }).subscribe({
            next: () => {
                this.dialogRef.close({success: true});
            },
            error: error => {
                Spinner.hide('eSignInProgress');
                this.handleError(error);
            },
        });
    }

    reissue(currentEsign: EsignResponse, keyPass: string): Observable<EsignResponse> {
        return this.createEsign(keyPass)
            .pipe(
                mergeMap((newEsign) => {
                    return this.esignApiService.revokeEsign({
                        keyName: currentEsign.keyName,
                        revocationKeyName: newEsign.keyName,
                        revocationKeyPass: keyPass,
                    }).pipe(map(() => {
                        return newEsign;
                    }));
                }),
            );
    }

    forgotCode(): void {
        if (this.dialogData.target === 'sign') {
            this.close({
                success: false,
                resetPassword: true,
            });
        } else {
            this.resetForDialogMode(ESignDialogMode.REISSUE_INFO);
        }
    }

    close(state?: ESignDialogResult): void {
        Spinner.hideAll();
        this.dialogRef.close(state);
    }

    downloadIssueAgreement = (): void => {
        let downloadRequest;
        if (
            this.dialogMode === ESignDialogMode.REISSUE_AND_SIGN ||
            this.dialogMode === ESignDialogMode.REISSUE ||
            this.dialogMode === ESignDialogMode.ISSUE_AND_SIGN
        ) {
            if (this.hasOrgSignScope) {
                downloadRequest = this.esignApiService.getUlIssueAgreement();
            } else {
                downloadRequest = this.esignApiService.getFlIssueAgreement();
            }
        }
        downloadRequest.subscribe(agreement => downloadBase64(agreement.content, agreement.contentType, agreement.name));
    };

    downloadRevocationAgreement = (): void => {
        let downloadRequest;
        if (this.dialogMode === ESignDialogMode.REISSUE_INFO) {
            if (this.hasOrgSignScope) {
                downloadRequest = this.esignApiService.getUlRevocationAgreement({revocationReason: RevocationReason.SUPERSEDED});
            } else {
                downloadRequest = this.esignApiService.getFlRevocationAgreement({revocationReason: RevocationReason.SUPERSEDED});
            }
        } else if (this.dialogMode === ESignDialogMode.REVOCATION) {
            if (this.hasOrgSignScope) {
                downloadRequest = this.esignApiService.getUlRevocationAgreement({
                    revocationReason: RevocationReason.CESSATION_OF_OPERATION,
                });
            } else {
                downloadRequest = this.esignApiService.getFlRevocationAgreement({
                    revocationReason: RevocationReason.CESSATION_OF_OPERATION,
                });
            }
        }
        downloadRequest.subscribe(agreement => downloadBase64(agreement.content, agreement.contentType, agreement.name));
    };

    private handleError(error: any): void {
        const businessError = toBusinessOrNull(error);
        if (businessError?.error === BusinessErrorCode.ESIGN_WRONG_PASSWORD) {
            this.analyticsService.capture(ESIGN_PIN_WRONG_PASSWORD, {esign_status: this.esignStatus});
            this.codeError = ESignDialogStatus.CODE_INVALID;
            this.attemptsLeft = businessError.additionalParams.availableAttemptsCount;
        } else {
            this.analyticsService.capture(ESIGN_SIGN_ERROR, {
                reason: businessError?.error || error?.message,
                esign_status: this.esignStatus,
            });
            this.close({
                success: false, error,
            });
        }
    }

    private resetForDialogMode(dialogMode: ESignDialogMode): void {
        this.dialogMode = dialogMode;
        this.isAcceptanceApplied = null;
        this.code = null;
        this.codeError = null;
        this.attemptsLeft = null;
    }

    private createEsign(newKeyPass: string): Observable<EsignResponse> {
        this.analyticsService.capture(ESIGN_ISSUE_STARTED);
        return this.esignApiService.createEsign({keyPass: newKeyPass}).pipe(
            tap(_ => this.analyticsService.capture(ESIGN_ISSUE_SUCCEED)),
            catchError(error => {
                this.analyticsService.capture(ESIGN_ISSUE_ERROR, error);
                throw error;
            }),
        );
    }
}

export interface ESignDialogData {
    esign: EsignResponse;
    hasOrgSignScope: boolean;
    target: 'revocation' | 'sign' | 'reissue';
    authorityMatrixId?: string;
    headFullName?: string;
    headPhone?: string;
    headEmail?: string;
    oauthSessionId?: string;
    phoneNumber?: string;
}

export enum ESignDialogStatus {
    OK = 'OK',
    CODE_INVALID = 'CODE_INVALID',
    CODES_NOT_MATCHED = 'CODES_NOT_MATCHED',
}

enum ESignDialogMode {
    REISSUE_INFO = 'REISSUE_INFO', // сбросить пароль
    SIGN = 'SIGN', // просто подписать
    ISSUE_AND_SIGN = 'ISSUE_AND_SIGN', // выпустить и подписать
    REISSUE_AND_SIGN = 'REISSUE_AND_SIGN', // забыли пароль - перевыпустить и подписать
    REVOCATION = 'REVOCATION', // просто отозвать
    REISSUE = 'REISSUE', // просто перевыпустить
}

export interface ESignDialogResult {
    resetPassword?: boolean;
    success: boolean;
    error?: any;
}
